import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import {
  createVoiceMutation,
  listVoicesQueryKey,
  useVoices,
  type VoiceView,
  voiceCreateErrorMessage,
  voiceLangLabels,
  voiceStatus,
} from '@/entities/voice'
import { cn, errorMessage, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CardLink } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectField, TextField } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { OwnerChip } from '@/shared/ui/owner-chip'
import { OWNER_FILTER_PLATFORM, OwnerFilter, ownerFilterAccount } from '@/shared/ui/owner-filter'
import { PageHeader } from '@/shared/ui/page-header'
import { SlideOver } from '@/shared/ui/slide-over'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'

export const Route = createFileRoute('/voices')({
  component: VoicesPage,
  staticData: { crumb: 'Голоса' },
})

// Отбор по владельцу: аккаунт — серверный `?account=`, «платформенные» — по
// пустому владельцу из каталога. Список владельцев для селекта — из каталога
// целиком, иначе отбор прятал бы собственные пункты.
function useVoiceCatalog(owner: string) {
  const catalog = useVoices()
  const account = ownerFilterAccount(owner)
  const byAccount = useVoices(account ? { account } : {})
  const owners = [...new Set(catalog.voices.map((v) => v.owner_account_id).filter(Boolean))]
  const voices =
    owner === OWNER_FILTER_PLATFORM
      ? catalog.voices.filter((v) => v.owner_account_id === '')
      : byAccount.voices
  return { ...byAccount, voices, owners, catalogEmpty: catalog.voices.length === 0 }
}

function VoicesPage() {
  const [owner, setOwner] = useState('')
  const { voices, owners, catalogEmpty, isPending, isError, error, refetch } =
    useVoiceCatalog(owner)
  const { voiceId } = useParams({ strict: false })
  const [addOpen, setAddOpen] = useState(false)

  return (
    <>
      <PageHeader
        title="Голоса"
        description="Каталог голосов клонирования: ref-клип + нормализованный текст для TTS"
        actions={
          <Button variant="accent" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={18} />
            Добавить голос
          </Button>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить голоса"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : catalogEmpty ? (
        <EmptyState
          icon="mic"
          title="Голосов пока нет"
          description="Загрузи клип 15–25 секунд ровной речи — остальное сделает юнит"
          action={
            <Button variant="accent" size="sm" onClick={() => setAddOpen(true)}>
              Добавить голос
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <OwnerFilter className="w-64" owners={owners} value={owner} onChange={setOwner} />
            <p className="text-caption text-muted">
              Платформенный голос видит каждое приложение, клиентский — только его аккаунт
            </p>
          </div>

          <div className="flex items-start gap-6">
            <div className="flex w-[400px] shrink-0 flex-col gap-2">
              {voices.length === 0 && (
                <EmptyState
                  icon="mic"
                  title="У этого владельца голосов нет"
                  action={
                    <Button size="sm" onClick={() => setOwner('')}>
                      Все владельцы
                    </Button>
                  }
                />
              )}
              {voices.map((voice) => (
                <VoiceCard key={voice.id} voice={voice} active={voiceId === voice.id} />
              ))}
            </div>

            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      )}

      <AddVoiceSlideOver open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  )
}

function VoiceCard({ voice, active }: { voice: VoiceView; active: boolean }) {
  return (
    <CardLink to="/voices/$voiceId" params={{ voiceId: voice.id }} active={active}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-body-sm font-medium">{voice.name}</span>
        <StatusBadge {...voiceStatus(voice.status)} />
      </div>
      <p className={cn('mt-0.5 truncate text-body-sm', active ? 'text-white/80' : 'text-muted')}>
        {voice.id}
      </p>
      <div
        className={cn(
          'mt-3 flex items-center justify-between gap-2 rounded-control px-3 py-2',
          active ? 'bg-black/25' : 'bg-raise',
        )}
      >
        <OwnerChip ownerAccountId={voice.owner_account_id} />
        <span className={cn('text-caption', active ? 'text-white/70' : 'text-muted')}>
          {formatDateTime(voice.created_at)}
        </span>
      </div>
    </CardLink>
  )
}

function AddVoiceSlideOver({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [owner, setOwner] = useState('')
  const [lang, setLang] = useState('ru')
  const [file, setFile] = useState<File | null>(null)
  const [created, setCreated] = useState<VoiceView | null>(null)

  const create = useMutation({
    ...createVoiceMutation(),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: listVoicesQueryKey() })
      setCreated(res.data)
    },
  })

  const close = () => {
    // Пока идёт транскрибация, закрытие потеряло бы результат — игнорируем.
    if (create.isPending) return
    create.reset()
    setName('')
    setOwner('')
    setLang('ru')
    setFile(null)
    setCreated(null)
    onClose()
  }

  return (
    <SlideOver
      open={open}
      onClose={close}
      title={created ? 'Голос заведён' : 'Новый голос'}
      footer={
        created ? (
          <Button variant="primary" onClick={close}>
            Готово
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={create.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              pending={create.isPending}
              disabled={!name.trim() || !file}
              onClick={() => {
                if (!file) return
                // Владелец задаётся один раз при заведении: пусто — платформенный.
                const ownerId = owner.trim()
                create.mutate({
                  query: {
                    name: name.trim(),
                    lang: lang as 'ru' | 'en' | 'ar',
                    ...(ownerId ? { owner_account_id: ownerId } : {}),
                  },
                  body: file,
                })
              }}
            >
              Добавить голос
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-body-md font-medium">{created.name}</p>
            <StatusBadge {...voiceStatus(created.status)} />
          </div>
          <div className="rounded-control bg-raise px-4 py-3">
            <p className="mb-1 text-caption font-medium uppercase tracking-[0.08em] text-faint">
              Нормализованный ref-текст
            </p>
            <p className="text-body-sm">{created.ref_text || '—'}</p>
          </div>
          <p className="text-caption text-muted">
            С этим текстом клип пойдёт в клонирование — проверь, что он совпадает с речью в записи.
            Если нет — удали голос и загрузи клип почище.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <TextField
            label="Название"
            required
            placeholder="Алиса · тёплый"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Владелец (аккаунт)"
            placeholder="client-204"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            hint="Пусто — платформенный голос, его видит каждое приложение; id аккаунта — клиентский, виден только ему"
          />
          <SelectField
            label="Язык речи в клипе"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
          >
            {Object.entries(voiceLangLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <div className="flex flex-col gap-1.5">
            <span className="text-body-sm text-muted">
              Аудио-клип
              <span className="ml-0.5 text-faint">*</span>
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="audio/*,.mp3,.ogg,.wav,.m4a"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="flex h-11 w-full items-center gap-2.5 rounded-control border border-transparent bg-raise px-4 text-left text-body-sm transition-colors hover:border-outline focus:border-outline focus:outline-none"
            >
              <Icon name="mic" size={16} className="shrink-0 text-muted" />
              {file ? (
                <span className="min-w-0 flex-1 truncate">
                  {file.name}
                  <span className="ml-1.5 text-faint">{formatFileSize(file.size)}</span>
                </span>
              ) : (
                <span className="flex-1 text-faint">Выбрать файл…</span>
              )}
            </button>
            <p className="text-caption text-faint">
              1–60 секунд, лучше 15–25 секунд ровной речи; mp3 / ogg / wav / m4a
            </p>
          </div>

          {create.isPending && (
            <div className="flex items-center gap-3 rounded-control bg-info/10 px-4 py-3 text-info">
              <Spinner size={18} />
              <div className="min-w-0">
                <p className="text-body-sm font-medium">Транскрибация на юните…</p>
                <p className="text-caption text-muted">
                  Транскод, распознавание на C-юните и LLM-нормализация — обычно 30–90 секунд, не
                  закрывай панель
                </p>
              </div>
            </div>
          )}
          {create.isError && (
            <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
              {voiceCreateErrorMessage(create.error)}
            </p>
          )}
        </div>
      )}
    </SlideOver>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
  return `${Math.max(1, Math.round(bytes / 1024))} КБ`
}
